// Render Kit cards in chat: email, email conversation and email draft.
// Namespace `render-kit`. English is the source; all 24 app languages are filled.
import type { NamespaceCatalog } from '@/i18n/ns';

const en = {
  emailCard: 'Email',
  threadCard: 'Email conversation',
  draftCard: 'Email draft',
  from: 'From',
  to: 'To',
  cc: 'Cc',
  bcc: 'Bcc',
  subject: 'Subject',
  noSubject: '(no subject)',
  message: 'Message',
  attachments: 'Attachments',
  showMore: 'Show more',
  showLess: 'Show less',
  messagesCount: 'one:message|other:messages',
  earlierOmitted: 'one:earlier message not shown|other:earlier messages not shown',
  showCcBcc: 'Cc and Bcc',
  addRecipient: 'Add an address',
  invalidAddress: 'Check the address {address}.',
  removeRecipient: 'Remove {address}',
  send: 'Send',
  sending: 'Sending…',
  sent: 'Sent',
  discard: 'Don’t send',
  discarded: 'Not sent',
  failed: 'Couldn’t send this email.',
  unknown:
    'Poneva couldn’t confirm whether this email was sent. Check Sent in {name} before trying again.',
  expired: 'This draft expired. Ask Poneva for a new one.',
  loading: 'Loading…',
  unavailable: 'This card couldn’t be shown.',
  replyKeepsSubject: '{name} keeps the conversation’s subject for a reply.',
  bodyShortened: 'Shortened here. Open it in Gmail for the rest.',
  writeAccessNeeded:
    '{name} is set to Read, so Poneva can’t send this email. Open Connections, set {name} to Read & write, then come back and select Send.',
  openConnections: 'Open Connections',
  writeAccessAsk:
    '{name} is set to Read, and only its owner or an admin can change that. Ask them to set {name} to Read & write, then select Send.',
};
export type RenderKitCopyKey = keyof typeof en;

const copy: NamespaceCatalog<RenderKitCopyKey> = {
  en,
  bg: {
    emailCard: 'Имейл',
    threadCard: 'Имейл разговор',
    draftCard: 'Чернова на имейл',
    from: 'От',
    to: 'До',
    cc: 'Копие',
    bcc: 'Скрито копие',
    subject: 'Тема',
    noSubject: '(без тема)',
    message: 'Съобщение',
    attachments: 'Прикачени файлове',
    showMore: 'Покажи още',
    showLess: 'Покажи по-малко',
    messagesCount: 'one:съобщение|other:съобщения',
    earlierOmitted: 'one:по-ранно съобщение не е показано|other:по-ранни съобщения не са показани',
    showCcBcc: 'Копие и скрито копие',
    addRecipient: 'Добавете адрес',
    invalidAddress: 'Проверете адреса {address}.',
    removeRecipient: 'Премахване на {address}',
    send: 'Изпращане',
    sending: 'Изпраща се…',
    sent: 'Изпратено',
    discard: 'Не изпращай',
    discarded: 'Не е изпратено',
    failed: 'Имейлът не можа да бъде изпратен.',
    unknown:
      'Poneva не можа да потвърди дали имейлът е изпратен. Проверете „Изпратени“ в {name}, преди да опитате отново.',
    expired: 'Тази чернова изтече. Помолете Poneva за нова.',
    loading: 'Зареждане…',
    unavailable: 'Тази карта не можа да бъде показана.',
    replyKeepsSubject: '{name} запазва темата на разговора при отговор.',
    bodyShortened: 'Съкратено тук. Отворете го в Gmail за останалото.',
    writeAccessNeeded:
      '{name} е настроен на Четене, затова Poneva не може да изпрати този имейл. Отворете Връзки, задайте {name} на Четене и писане, след това се върнете и изберете Изпращане.',
    openConnections: 'Отваряне на Връзки',
    writeAccessAsk:
      '{name} е настроен на Четене и само собственикът му или администратор може да промени това. Помолете ги да зададат {name} на Четене и писане, след това изберете Изпращане.',
  },
  hr: {
    emailCard: 'E-pošta',
    threadCard: 'Razgovor e-poštom',
    draftCard: 'Skica e-pošte',
    from: 'Od',
    to: 'Za',
    cc: 'Kopija',
    bcc: 'Skrivena kopija',
    subject: 'Predmet',
    noSubject: '(bez predmeta)',
    message: 'Poruka',
    attachments: 'Privici',
    showMore: 'Prikaži više',
    showLess: 'Prikaži manje',
    messagesCount: 'one:poruka|few:poruke|other:poruka',
    earlierOmitted:
      'one:ranija poruka nije prikazana|few:ranije poruke nisu prikazane|other:ranijih poruka nije prikazano',
    showCcBcc: 'Kopija i skrivena kopija',
    addRecipient: 'Dodajte adresu',
    invalidAddress: 'Provjerite adresu {address}.',
    removeRecipient: 'Ukloni {address}',
    send: 'Pošalji',
    sending: 'Šalje se…',
    sent: 'Poslano',
    discard: 'Nemoj slati',
    discarded: 'Nije poslano',
    failed: 'Ova e-pošta nije mogla biti poslana.',
    unknown:
      'Poneva nije mogla potvrditi je li ova e-pošta poslana. Provjerite Poslano u {name} prije ponovnog pokušaja.',
    expired: 'Ova je skica istekla. Zatražite novu od Ponevae.',
    loading: 'Učitavanje…',
    unavailable: 'Ova kartica nije mogla biti prikazana.',
    replyKeepsSubject: '{name} zadržava predmet razgovora u odgovoru.',
    bodyShortened: 'Ovdje je skraćeno. Ostatak otvorite u Gmailu.',
    writeAccessNeeded:
      '{name} je postavljen na Čitanje, pa Poneva ne može poslati ovu e-poruku. Otvorite Veze, postavite {name} na Čitanje i pisanje, zatim se vratite i odaberite Pošalji.',
    openConnections: 'Otvori Veze',
    writeAccessAsk:
      '{name} je postavljen na Čitanje i to može promijeniti samo njegov vlasnik ili administrator. Zamolite ih da postave {name} na Čitanje i pisanje, zatim odaberite Pošalji.',
  },
  cs: {
    emailCard: 'E-mail',
    threadCard: 'E-mailová konverzace',
    draftCard: 'Koncept e-mailu',
    from: 'Od',
    to: 'Komu',
    cc: 'Kopie',
    bcc: 'Skrytá kopie',
    subject: 'Předmět',
    noSubject: '(bez předmětu)',
    message: 'Zpráva',
    attachments: 'Přílohy',
    showMore: 'Zobrazit více',
    showLess: 'Zobrazit méně',
    messagesCount: 'one:zpráva|few:zprávy|many:zprávy|other:zpráv',
    earlierOmitted:
      'one:dřívější zpráva není zobrazena|few:dřívější zprávy nejsou zobrazeny|many:dřívější zprávy nejsou zobrazeny|other:dřívějších zpráv není zobrazeno',
    showCcBcc: 'Kopie a skrytá kopie',
    addRecipient: 'Přidat adresu',
    invalidAddress: 'Zkontrolujte adresu {address}.',
    removeRecipient: 'Odebrat {address}',
    send: 'Odeslat',
    sending: 'Odesílání…',
    sent: 'Odesláno',
    discard: 'Neodesílat',
    discarded: 'Neodesláno',
    failed: 'Tento e-mail se nepodařilo odeslat.',
    unknown:
      'Poneva nemohla ověřit, zda byl tento e-mail odeslán. Než to zkusíte znovu, zkontrolujte Odeslané v {name}.',
    expired: 'Platnost tohoto konceptu vypršela. Požádejte Ponevu o nový.',
    loading: 'Načítání…',
    unavailable: 'Tuto kartu se nepodařilo zobrazit.',
    replyKeepsSubject: '{name} u odpovědi ponechá předmět konverzace.',
    bodyShortened: 'Zde je zkráceno. Zbytek otevřete v Gmailu.',
    writeAccessNeeded:
      '{name} je nastaven na Čtení, takže Poneva tento e-mail nemůže odeslat. Otevřete Připojení, nastavte {name} na Čtení a zápis, pak se vraťte a zvolte Odeslat.',
    openConnections: 'Otevřít Připojení',
    writeAccessAsk:
      '{name} je nastaven na Čtení a změnit to může jen jeho vlastník nebo správce. Požádejte je, aby {name} nastavili na Čtení a zápis, a pak zvolte Odeslat.',
  },
  da: {
    emailCard: 'E-mail',
    threadCard: 'E-mailsamtale',
    draftCard: 'E-mailudkast',
    from: 'Fra',
    to: 'Til',
    cc: 'Cc',
    bcc: 'Bcc',
    subject: 'Emne',
    noSubject: '(intet emne)',
    message: 'Besked',
    attachments: 'Vedhæftede filer',
    showMore: 'Vis mere',
    showLess: 'Vis mindre',
    messagesCount: 'one:besked|other:beskeder',
    earlierOmitted: 'one:tidligere besked vises ikke|other:tidligere beskeder vises ikke',
    showCcBcc: 'Cc og Bcc',
    addRecipient: 'Tilføj en adresse',
    invalidAddress: 'Tjek adressen {address}.',
    removeRecipient: 'Fjern {address}',
    send: 'Send',
    sending: 'Sender…',
    sent: 'Sendt',
    discard: 'Send ikke',
    discarded: 'Ikke sendt',
    failed: 'Mailen kunne ikke sendes.',
    unknown:
      'Poneva kunne ikke bekræfte, om mailen blev sendt. Tjek Sendt i {name}, før du prøver igen.',
    expired: 'Udkastet er udløbet. Bed Poneva om et nyt.',
    loading: 'Indlæser…',
    unavailable: 'Kortet kunne ikke vises.',
    replyKeepsSubject: '{name} beholder samtalens emne i et svar.',
    bodyShortened: 'Forkortet her. Åbn den i Gmail for at se resten.',
    writeAccessNeeded:
      '{name} er sat til Læsning, så Poneva kan ikke sende denne e-mail. Åbn Forbindelser, sæt {name} til Læsning og skrivning, og kom så tilbage og vælg Send.',
    openConnections: 'Åbn Forbindelser',
    writeAccessAsk:
      '{name} er sat til Læsning, og kun ejeren eller en administrator kan ændre det. Bed dem om at sætte {name} til Læsning og skrivning, og vælg så Send.',
  },
  nl: {
    emailCard: 'E-mail',
    threadCard: 'E-mailgesprek',
    draftCard: 'Concept-e-mail',
    from: 'Van',
    to: 'Aan',
    cc: 'Cc',
    bcc: 'Bcc',
    subject: 'Onderwerp',
    noSubject: '(geen onderwerp)',
    message: 'Bericht',
    attachments: 'Bijlagen',
    showMore: 'Meer tonen',
    showLess: 'Minder tonen',
    messagesCount: 'one:bericht|other:berichten',
    earlierOmitted: 'one:eerder bericht niet getoond|other:eerdere berichten niet getoond',
    showCcBcc: 'Cc en Bcc',
    addRecipient: 'Adres toevoegen',
    invalidAddress: 'Controleer het adres {address}.',
    removeRecipient: '{address} verwijderen',
    send: 'Versturen',
    sending: 'Versturen…',
    sent: 'Verstuurd',
    discard: 'Niet versturen',
    discarded: 'Niet verstuurd',
    failed: 'Deze e-mail kon niet worden verstuurd.',
    unknown:
      'Poneva kon niet bevestigen of deze e-mail is verstuurd. Controleer Verzonden in {name} voordat je het opnieuw probeert.',
    expired: 'Dit concept is verlopen. Vraag Poneva om een nieuw concept.',
    loading: 'Laden…',
    unavailable: 'Deze kaart kon niet worden getoond.',
    replyKeepsSubject: '{name} behoudt bij een antwoord het onderwerp van het gesprek.',
    bodyShortened: 'Hier ingekort. Open hem in Gmail voor de rest.',
    writeAccessNeeded:
      '{name} staat op Lezen, dus Poneva kan deze e-mail niet versturen. Open Verbindingen, zet {name} op Lezen en schrijven, kom dan terug en kies Versturen.',
    openConnections: 'Verbindingen openen',
    writeAccessAsk:
      '{name} staat op Lezen en alleen de eigenaar of een beheerder kan dat wijzigen. Vraag hen {name} op Lezen en schrijven te zetten en kies dan Versturen.',
  },
  et: {
    emailCard: 'E-kiri',
    threadCard: 'E-kirjavestlus',
    draftCard: 'E-kirja mustand',
    from: 'Saatja',
    to: 'Saaja',
    cc: 'Koopia',
    bcc: 'Pimekoopia',
    subject: 'Teema',
    noSubject: '(teemata)',
    message: 'Sõnum',
    attachments: 'Manused',
    showMore: 'Näita rohkem',
    showLess: 'Näita vähem',
    messagesCount: 'one:sõnum|other:sõnumit',
    earlierOmitted: 'one:varasem sõnum pole näidatud|other:varasemat sõnumit pole näidatud',
    showCcBcc: 'Koopia ja pimekoopia',
    addRecipient: 'Lisa aadress',
    invalidAddress: 'Kontrolli aadressi {address}.',
    removeRecipient: 'Eemalda {address}',
    send: 'Saada',
    sending: 'Saadan…',
    sent: 'Saadetud',
    discard: 'Ära saada',
    discarded: 'Saatmata',
    failed: 'Seda e-kirja ei õnnestunud saata.',
    unknown:
      'Poneva ei saanud kinnitada, kas see e-kiri saadeti. Enne uuesti proovimist kontrolli kausta Saadetud kontol {name}.',
    expired: 'See mustand on aegunud. Palu Ponevalt uus.',
    loading: 'Laadin…',
    unavailable: 'Seda kaarti ei õnnestunud näidata.',
    replyKeepsSubject: '{name} jätab vastusele vestluse teema.',
    bodyShortened: 'Siin lühendatud. Ülejäänu nägemiseks ava see Gmailis.',
    writeAccessNeeded:
      '{name} on seatud valikule Lugemine, seega Poneva ei saa seda e-kirja saata. Ava Ühendused, sea {name} valikule Lugemine ja kirjutamine, siis tule tagasi ja vali Saada.',
    openConnections: 'Ava ühendused',
    writeAccessAsk:
      '{name} on seatud valikule Lugemine ja seda saab muuta ainult selle omanik või administraator. Palu neil seada {name} valikule Lugemine ja kirjutamine, siis vali Saada.',
  },
  fi: {
    emailCard: 'Sähköposti',
    threadCard: 'Sähköpostikeskustelu',
    draftCard: 'Sähköpostiluonnos',
    from: 'Lähettäjä',
    to: 'Vastaanottaja',
    cc: 'Kopio',
    bcc: 'Piilokopio',
    subject: 'Aihe',
    noSubject: '(ei aihetta)',
    message: 'Viesti',
    attachments: 'Liitteet',
    showMore: 'Näytä lisää',
    showLess: 'Näytä vähemmän',
    messagesCount: 'one:viesti|other:viestiä',
    earlierOmitted: 'one:aiempi viesti ei näy|other:aiempaa viestiä ei näy',
    showCcBcc: 'Kopio ja piilokopio',
    addRecipient: 'Lisää osoite',
    invalidAddress: 'Tarkista osoite {address}.',
    removeRecipient: 'Poista {address}',
    send: 'Lähetä',
    sending: 'Lähetetään…',
    sent: 'Lähetetty',
    discard: 'Älä lähetä',
    discarded: 'Ei lähetetty',
    failed: 'Sähköpostia ei voitu lähettää.',
    unknown:
      'Poneva ei voinut varmistaa, lähetettiinkö sähköposti. Tarkista Lähetetyt palvelussa {name} ennen kuin yrität uudelleen.',
    expired: 'Luonnos vanheni. Pyydä Ponevalta uusi.',
    loading: 'Ladataan…',
    unavailable: 'Korttia ei voitu näyttää.',
    replyKeepsSubject: '{name} säilyttää vastauksessa keskustelun aiheen.',
    bodyShortened: 'Lyhennetty tässä. Avaa se Gmailissa nähdäksesi loput.',
    writeAccessNeeded:
      '{name} on asetettu tilaan Lukeminen, joten Poneva ei voi lähettää tätä sähköpostia. Avaa Yhteydet, aseta {name} tilaan Lukeminen ja kirjoittaminen, palaa sitten tänne ja valitse Lähetä.',
    openConnections: 'Avaa Yhteydet',
    writeAccessAsk:
      '{name} on asetettu tilaan Lukeminen, ja vain sen omistaja tai ylläpitäjä voi muuttaa sitä. Pyydä heitä asettamaan {name} tilaan Lukeminen ja kirjoittaminen ja valitse sitten Lähetä.',
  },
  fr: {
    emailCard: 'E-mail',
    threadCard: 'Conversation par e-mail',
    draftCard: 'Brouillon d’e-mail',
    from: 'De',
    to: 'À',
    cc: 'Cc',
    bcc: 'Cci',
    subject: 'Objet',
    noSubject: '(sans objet)',
    message: 'Message',
    attachments: 'Pièces jointes',
    showMore: 'Afficher plus',
    showLess: 'Afficher moins',
    messagesCount: 'one:message|other:messages',
    earlierOmitted: 'one:message précédent non affiché|other:messages précédents non affichés',
    showCcBcc: 'Cc et Cci',
    addRecipient: 'Ajouter une adresse',
    invalidAddress: 'Vérifiez l’adresse {address}.',
    removeRecipient: 'Retirer {address}',
    send: 'Envoyer',
    sending: 'Envoi…',
    sent: 'Envoyé',
    discard: 'Ne pas envoyer',
    discarded: 'Non envoyé',
    failed: 'Impossible d’envoyer cet e-mail.',
    unknown:
      'Poneva n’a pas pu confirmer l’envoi de cet e-mail. Vérifiez les éléments envoyés dans {name} avant de réessayer.',
    expired: 'Ce brouillon a expiré. Demandez-en un nouveau à Poneva.',
    loading: 'Chargement…',
    unavailable: 'Impossible d’afficher cette carte.',
    replyKeepsSubject: '{name} conserve l’objet de la conversation pour une réponse.',
    bodyShortened: 'Raccourci ici. Ouvrez-le dans Gmail pour lire la suite.',
    writeAccessNeeded:
      '{name} est réglé sur Lecture : Poneva ne peut donc pas envoyer cet e-mail. Ouvrez Connexions, réglez {name} sur Lecture et écriture, puis revenez et choisissez Envoyer.',
    openConnections: 'Ouvrir Connexions',
    writeAccessAsk:
      '{name} est réglé sur Lecture, et seul son propriétaire ou un administrateur peut le modifier. Demandez-leur de régler {name} sur Lecture et écriture, puis choisissez Envoyer.',
  },
  de: {
    emailCard: 'E-Mail',
    threadCard: 'E-Mail-Verlauf',
    draftCard: 'E-Mail-Entwurf',
    from: 'Von',
    to: 'An',
    cc: 'Cc',
    bcc: 'Bcc',
    subject: 'Betreff',
    noSubject: '(kein Betreff)',
    message: 'Nachricht',
    attachments: 'Anhänge',
    showMore: 'Mehr anzeigen',
    showLess: 'Weniger anzeigen',
    messagesCount: 'one:Nachricht|other:Nachrichten',
    earlierOmitted:
      'one:frühere Nachricht nicht angezeigt|other:frühere Nachrichten nicht angezeigt',
    showCcBcc: 'Cc und Bcc',
    addRecipient: 'Adresse hinzufügen',
    invalidAddress: 'Prüfen Sie die Adresse {address}.',
    removeRecipient: '{address} entfernen',
    send: 'Senden',
    sending: 'Wird gesendet…',
    sent: 'Gesendet',
    discard: 'Nicht senden',
    discarded: 'Nicht gesendet',
    failed: 'Diese E-Mail konnte nicht gesendet werden.',
    unknown:
      'Poneva konnte nicht bestätigen, ob diese E-Mail gesendet wurde. Prüfen Sie „Gesendet“ in {name}, bevor Sie es erneut versuchen.',
    expired: 'Dieser Entwurf ist abgelaufen. Bitten Sie Poneva um einen neuen.',
    loading: 'Wird geladen…',
    unavailable: 'Diese Karte konnte nicht angezeigt werden.',
    replyKeepsSubject: '{name} behält bei einer Antwort den Betreff der Unterhaltung bei.',
    bodyShortened: 'Hier gekürzt. Öffnen Sie sie in Gmail, um den Rest zu lesen.',
    writeAccessNeeded:
      '{name} ist auf Lesen eingestellt, daher kann Poneva diese E-Mail nicht senden. Öffnen Sie Verbindungen, stellen Sie {name} auf Lesen und Schreiben, kommen Sie dann zurück und wählen Sie Senden.',
    openConnections: 'Verbindungen öffnen',
    writeAccessAsk:
      '{name} ist auf Lesen eingestellt, und nur der Inhaber oder ein Admin kann das ändern. Bitten Sie sie, {name} auf Lesen und Schreiben zu stellen, und wählen Sie dann Senden.',
  },
  el: {
    emailCard: 'Email',
    threadCard: 'Συνομιλία email',
    draftCard: 'Πρόχειρο email',
    from: 'Από',
    to: 'Προς',
    cc: 'Κοιν.',
    bcc: 'Κρυφή κοιν.',
    subject: 'Θέμα',
    noSubject: '(χωρίς θέμα)',
    message: 'Μήνυμα',
    attachments: 'Συνημμένα',
    showMore: 'Εμφάνιση περισσότερων',
    showLess: 'Εμφάνιση λιγότερων',
    messagesCount: 'one:μήνυμα|other:μηνύματα',
    earlierOmitted:
      'one:προηγούμενο μήνυμα δεν εμφανίζεται|other:προηγούμενα μηνύματα δεν εμφανίζονται',
    showCcBcc: 'Κοιν. και κρυφή κοιν.',
    addRecipient: 'Προσθήκη διεύθυνσης',
    invalidAddress: 'Ελέγξτε τη διεύθυνση {address}.',
    removeRecipient: 'Αφαίρεση {address}',
    send: 'Αποστολή',
    sending: 'Αποστολή σε εξέλιξη…',
    sent: 'Στάλθηκε',
    discard: 'Να μη σταλεί',
    discarded: 'Δεν στάλθηκε',
    failed: 'Δεν ήταν δυνατή η αποστολή αυτού του email.',
    unknown:
      'Το Poneva δεν μπόρεσε να επιβεβαιώσει αν στάλθηκε αυτό το email. Ελέγξτε τα Απεσταλμένα στο {name} πριν δοκιμάσετε ξανά.',
    expired: 'Αυτό το πρόχειρο έληξε. Ζητήστε ένα νέο από το Poneva.',
    loading: 'Φόρτωση…',
    unavailable: 'Δεν ήταν δυνατή η εμφάνιση αυτής της κάρτας.',
    replyKeepsSubject: 'Το {name} κρατά το θέμα της συνομιλίας στην απάντηση.',
    bodyShortened: 'Συντομευμένο εδώ. Ανοίξτε το στο Gmail για τα υπόλοιπα.',
    writeAccessNeeded:
      'Το {name} έχει οριστεί σε Ανάγνωση, οπότε το Poneva δεν μπορεί να στείλει αυτό το email. Ανοίξτε τις Συνδέσεις, ορίστε το {name} σε Ανάγνωση και εγγραφή, έπειτα επιστρέψτε και επιλέξτε Αποστολή.',
    openConnections: 'Άνοιγμα Συνδέσεων',
    writeAccessAsk:
      'Το {name} έχει οριστεί σε Ανάγνωση και μόνο ο κάτοχός του ή ένας διαχειριστής μπορεί να το αλλάξει. Ζητήστε τους να ορίσουν το {name} σε Ανάγνωση και εγγραφή και έπειτα επιλέξτε Αποστολή.',
  },
  hu: {
    emailCard: 'E-mail',
    threadCard: 'E-mail-beszélgetés',
    draftCard: 'E-mail-piszkozat',
    from: 'Feladó',
    to: 'Címzett',
    cc: 'Másolat',
    bcc: 'Titkos másolat',
    subject: 'Tárgy',
    noSubject: '(nincs tárgy)',
    message: 'Üzenet',
    attachments: 'Mellékletek',
    showMore: 'Több megjelenítése',
    showLess: 'Kevesebb megjelenítése',
    messagesCount: 'one:üzenet|other:üzenet',
    earlierOmitted: 'one:korábbi üzenet nem látható|other:korábbi üzenet nem látható',
    showCcBcc: 'Másolat és titkos másolat',
    addRecipient: 'Cím hozzáadása',
    invalidAddress: 'Ellenőrizze ezt a címet: {address}.',
    removeRecipient: '{address} eltávolítása',
    send: 'Küldés',
    sending: 'Küldés…',
    sent: 'Elküldve',
    discard: 'Ne küldje el',
    discarded: 'Nincs elküldve',
    failed: 'Ezt az e-mailt nem sikerült elküldeni.',
    unknown:
      'A Poneva nem tudta megerősíteni, hogy az e-mail elment-e. Újrapróbálás előtt nézze meg az Elküldött mappát itt: {name}.',
    expired: 'Ez a piszkozat lejárt. Kérjen újat a Ponevától.',
    loading: 'Betöltés…',
    unavailable: 'Ezt a kártyát nem sikerült megjeleníteni.',
    replyKeepsSubject: 'Válasznál a(z) {name} megtartja a beszélgetés tárgyát.',
    bodyShortened: 'Itt rövidítve. A többihez nyissa meg a Gmailben.',
    writeAccessNeeded:
      'A(z) {name} Olvasás módra van állítva, ezért a Poneva nem tudja elküldeni ezt az e-mailt. Nyissa meg a Kapcsolatokat, állítsa a(z) {name} beállítását Olvasás és írás módra, majd térjen vissza, és válassza a Küldés lehetőséget.',
    openConnections: 'Kapcsolatok megnyitása',
    writeAccessAsk:
      'A(z) {name} Olvasás módra van állítva, és ezt csak a tulajdonosa vagy egy rendszergazda módosíthatja. Kérje meg őket, hogy állítsák a(z) {name} beállítását Olvasás és írás módra, majd válassza a Küldés lehetőséget.',
  },
  ga: {
    emailCard: 'Ríomhphost',
    threadCard: 'Comhrá ríomhphoist',
    draftCard: 'Dréacht ríomhphoist',
    from: 'Ó',
    to: 'Chuig',
    cc: 'Cc',
    bcc: 'Bcc',
    subject: 'Ábhar',
    noSubject: '(gan ábhar)',
    message: 'Teachtaireacht',
    attachments: 'Ceangaltáin',
    showMore: 'Taispeáin níos mó',
    showLess: 'Taispeáin níos lú',
    messagesCount:
      'one:teachtaireacht|two:theachtaireacht|few:theachtaireacht|many:dteachtaireacht|other:teachtaireacht',
    earlierOmitted:
      'one:teachtaireacht níos luaithe nach dtaispeántar|other:teachtaireachtaí níos luaithe nach dtaispeántar',
    showCcBcc: 'Cc agus Bcc',
    addRecipient: 'Cuir seoladh leis',
    invalidAddress: 'Seiceáil an seoladh {address}.',
    removeRecipient: 'Bain {address}',
    send: 'Seol',
    sending: 'Á sheoladh…',
    sent: 'Seolta',
    discard: 'Ná seol',
    discarded: 'Níor seoladh',
    failed: 'Níorbh fhéidir an ríomhphost seo a sheoladh.',
    unknown:
      'Níorbh fhéidir le Poneva a dhearbhú ar seoladh an ríomhphost seo. Seiceáil Seolta in {name} sula ndéanann tú iarracht arís.',
    expired: 'Tá an dréacht seo imithe in éag. Iarr ceann nua ar Poneva.',
    loading: 'Á lódáil…',
    unavailable: 'Níorbh fhéidir an cárta seo a thaispeáint.',
    replyKeepsSubject: 'Coinníonn {name} ábhar an chomhrá i bhfreagra.',
    bodyShortened: 'Giorraithe anseo. Oscail i Gmail é chun an chuid eile a fheiceáil.',
    writeAccessNeeded:
      'Tá {name} socraithe ar Léamh, mar sin ní féidir le Poneva an ríomhphost seo a sheoladh. Oscail Ceangail, socraigh {name} ar Léamh agus scríobh, ansin fill ar ais agus roghnaigh Seol.',
    openConnections: 'Oscail Ceangail',
    writeAccessAsk:
      'Tá {name} socraithe ar Léamh, agus ní féidir é sin a athrú ach ag an úinéir nó ag riarthóir. Iarr orthu {name} a shocrú ar Léamh agus scríobh, ansin roghnaigh Seol.',
  },
  it: {
    emailCard: 'Email',
    threadCard: 'Conversazione email',
    draftCard: 'Bozza email',
    from: 'Da',
    to: 'A',
    cc: 'Cc',
    bcc: 'Ccn',
    subject: 'Oggetto',
    noSubject: '(nessun oggetto)',
    message: 'Messaggio',
    attachments: 'Allegati',
    showMore: 'Mostra di più',
    showLess: 'Mostra meno',
    messagesCount: 'one:messaggio|other:messaggi',
    earlierOmitted: 'one:messaggio precedente non mostrato|other:messaggi precedenti non mostrati',
    showCcBcc: 'Cc e Ccn',
    addRecipient: 'Aggiungi un indirizzo',
    invalidAddress: 'Controlla l’indirizzo {address}.',
    removeRecipient: 'Rimuovi {address}',
    send: 'Invia',
    sending: 'Invio in corso…',
    sent: 'Inviata',
    discard: 'Non inviare',
    discarded: 'Non inviata',
    failed: 'Impossibile inviare questa email.',
    unknown:
      'Poneva non è riuscita a confermare se questa email è stata inviata. Controlla la posta inviata in {name} prima di riprovare.',
    expired: 'Questa bozza è scaduta. Chiedine una nuova a Poneva.',
    loading: 'Caricamento…',
    unavailable: 'Impossibile mostrare questa scheda.',
    replyKeepsSubject: '{name} mantiene l’oggetto della conversazione nelle risposte.',
    bodyShortened: 'Abbreviata qui. Aprila in Gmail per il resto.',
    writeAccessNeeded:
      '{name} è impostato su Lettura, quindi Poneva non può inviare questa email. Apri Connessioni, imposta {name} su Lettura e scrittura, poi torna qui e scegli Invia.',
    openConnections: 'Apri Connessioni',
    writeAccessAsk:
      '{name} è impostato su Lettura e solo il proprietario o un amministratore può cambiarlo. Chiedi loro di impostare {name} su Lettura e scrittura, poi scegli Invia.',
  },
  lv: {
    emailCard: 'E-pasts',
    threadCard: 'E-pasta saruna',
    draftCard: 'E-pasta melnraksts',
    from: 'No',
    to: 'Kam',
    cc: 'Kopija',
    bcc: 'Diskrētā kopija',
    subject: 'Temats',
    noSubject: '(bez temata)',
    message: 'Ziņojums',
    attachments: 'Pielikumi',
    showMore: 'Rādīt vairāk',
    showLess: 'Rādīt mazāk',
    messagesCount: 'zero:ziņojumu|one:ziņojums|other:ziņojumi',
    earlierOmitted:
      'zero:agrāku ziņojumu nav parādīts|one:agrāks ziņojums nav parādīts|other:agrāki ziņojumi nav parādīti',
    showCcBcc: 'Kopija un diskrētā kopija',
    addRecipient: 'Pievienot adresi',
    invalidAddress: 'Pārbaudiet adresi {address}.',
    removeRecipient: 'Noņemt {address}',
    send: 'Sūtīt',
    sending: 'Sūta…',
    sent: 'Nosūtīts',
    discard: 'Nesūtīt',
    discarded: 'Nav nosūtīts',
    failed: 'Šo e-pastu neizdevās nosūtīt.',
    unknown:
      'Poneva nevarēja apstiprināt, vai šis e-pasts tika nosūtīts. Pirms mēģināt vēlreiz, pārbaudiet Nosūtītos kontā {name}.',
    expired: 'Šī melnraksta derīgums ir beidzies. Palūdziet Ponevai jaunu.',
    loading: 'Ielādē…',
    unavailable: 'Šo karti neizdevās parādīt.',
    replyKeepsSubject: '{name} atbildē saglabā sarunas tematu.',
    bodyShortened: 'Šeit saīsināts. Atveriet to Gmail, lai redzētu pārējo.',
    writeAccessNeeded:
      '{name} ir iestatīts uz Lasīšana, tāpēc Poneva nevar nosūtīt šo e-pastu. Atveriet Savienojumus, iestatiet {name} uz Lasīšana un rakstīšana, tad atgriezieties un izvēlieties Sūtīt.',
    openConnections: 'Atvērt Savienojumus',
    writeAccessAsk:
      '{name} ir iestatīts uz Lasīšana, un to var mainīt tikai īpašnieks vai administrators. Palūdziet viņiem iestatīt {name} uz Lasīšana un rakstīšana, tad izvēlieties Sūtīt.',
  },
  lt: {
    emailCard: 'El. laiškas',
    threadCard: 'El. laiškų pokalbis',
    draftCard: 'El. laiško juodraštis',
    from: 'Nuo',
    to: 'Kam',
    cc: 'Kopija',
    bcc: 'Slapta kopija',
    subject: 'Tema',
    noSubject: '(be temos)',
    message: 'Žinutė',
    attachments: 'Priedai',
    showMore: 'Rodyti daugiau',
    showLess: 'Rodyti mažiau',
    messagesCount: 'one:žinutė|few:žinutės|many:žinutės|other:žinučių',
    earlierOmitted:
      'one:ankstesnė žinutė nerodoma|few:ankstesnės žinutės nerodomos|many:ankstesnės žinutės nerodomos|other:ankstesnių žinučių nerodoma',
    showCcBcc: 'Kopija ir slapta kopija',
    addRecipient: 'Pridėti adresą',
    invalidAddress: 'Patikrinkite adresą {address}.',
    removeRecipient: 'Pašalinti {address}',
    send: 'Siųsti',
    sending: 'Siunčiama…',
    sent: 'Išsiųsta',
    discard: 'Nesiųsti',
    discarded: 'Neišsiųsta',
    failed: 'Šio el. laiško nepavyko išsiųsti.',
    unknown:
      'Poneva negalėjo patvirtinti, ar šis el. laiškas buvo išsiųstas. Prieš bandydami dar kartą, patikrinkite Išsiųstus paskyroje {name}.',
    expired: 'Šio juodraščio galiojimas baigėsi. Paprašykite Ponevos naujo.',
    loading: 'Įkeliama…',
    unavailable: 'Šios kortelės nepavyko parodyti.',
    replyKeepsSubject: '{name} atsakyme palieka pokalbio temą.',
    bodyShortened: 'Čia sutrumpinta. Likusią dalį atsiverskite „Gmail“.',
    writeAccessNeeded:
      '{name} nustatyta Skaitymas, todėl Poneva negali išsiųsti šio el. laiško. Atidarykite Jungtis, nustatykite {name} į Skaitymas ir rašymas, tada grįžkite ir pasirinkite Siųsti.',
    openConnections: 'Atidaryti Jungtis',
    writeAccessAsk:
      '{name} nustatyta Skaitymas, ir tai pakeisti gali tik savininkas arba administratorius. Paprašykite jų nustatyti {name} į Skaitymas ir rašymas, tada pasirinkite Siųsti.',
  },
  mt: {
    emailCard: 'Email',
    threadCard: 'Konversazzjoni bl-email',
    draftCard: 'Abbozz ta’ email',
    from: 'Mingħand',
    to: 'Lil',
    cc: 'Kopja',
    bcc: 'Kopja moħbija',
    subject: 'Suġġett',
    noSubject: '(mingħajr suġġett)',
    message: 'Messaġġ',
    attachments: 'Annessi',
    showMore: 'Uri aktar',
    showLess: 'Uri inqas',
    messagesCount: 'one:messaġġ|other:messaġġi',
    earlierOmitted: 'one:messaġġ preċedenti mhux muri|other:messaġġi preċedenti mhux murija',
    showCcBcc: 'Kopja u kopja moħbija',
    addRecipient: 'Żid indirizz',
    invalidAddress: 'Iċċekkja l-indirizz {address}.',
    removeRecipient: 'Neħħi {address}',
    send: 'Ibgħat',
    sending: 'Qed tintbagħat…',
    sent: 'Mibgħuta',
    discard: 'Tibgħatx',
    discarded: 'Mhux mibgħuta',
    failed: 'Din l-email ma setgħetx tintbagħat.',
    unknown:
      'Poneva ma setgħetx tikkonferma jekk din l-email intbagħatitx. Iċċekkja l-Mibgħuta f’{name} qabel ma terġa’ tipprova.',
    expired: 'Dan l-abbozz skada. Itlob wieħed ġdid lil Poneva.',
    loading: 'Qed jitgħabba…',
    unavailable: 'Din il-karta ma setgħetx tintwera.',
    replyKeepsSubject: '{name} iżomm is-suġġett tal-konversazzjoni f’tweġiba.',
    bodyShortened: 'Imqassar hawn. Iftaħha f’Gmail għall-bqija.',
    writeAccessNeeded:
      '{name} huwa ssettjat fuq Qari, għalhekk Poneva ma tistax tibgħat din l-email. Iftaħ Konnessjonijiet, issettja {name} fuq Qari u kitba, imbagħad erġa’ lura u agħżel Ibgħat.',
    openConnections: 'Iftaħ Konnessjonijiet',
    writeAccessAsk:
      '{name} huwa ssettjat fuq Qari, u dan jista’ jibdlu biss is-sid jew amministratur. Itlobhom jissettjaw {name} fuq Qari u kitba, imbagħad agħżel Ibgħat.',
  },
  pl: {
    emailCard: 'E-mail',
    threadCard: 'Rozmowa e-mailowa',
    draftCard: 'Wersja robocza e-maila',
    from: 'Od',
    to: 'Do',
    cc: 'DW',
    bcc: 'UDW',
    subject: 'Temat',
    noSubject: '(bez tematu)',
    message: 'Wiadomość',
    attachments: 'Załączniki',
    showMore: 'Pokaż więcej',
    showLess: 'Pokaż mniej',
    messagesCount: 'one:wiadomość|few:wiadomości|many:wiadomości|other:wiadomości',
    earlierOmitted:
      'one:wcześniejsza wiadomość niewidoczna|few:wcześniejsze wiadomości niewidoczne|many:wcześniejszych wiadomości niewidocznych|other:wcześniejszej wiadomości niewidocznej',
    showCcBcc: 'DW i UDW',
    addRecipient: 'Dodaj adres',
    invalidAddress: 'Sprawdź adres {address}.',
    removeRecipient: 'Usuń {address}',
    send: 'Wyślij',
    sending: 'Wysyłanie…',
    sent: 'Wysłano',
    discard: 'Nie wysyłaj',
    discarded: 'Nie wysłano',
    failed: 'Nie udało się wysłać tego e-maila.',
    unknown:
      'Poneva nie mogła potwierdzić, czy ten e-mail został wysłany. Zanim spróbujesz ponownie, sprawdź Wysłane w {name}.',
    expired: 'Ta wersja robocza wygasła. Poproś Ponevę o nową.',
    loading: 'Wczytywanie…',
    unavailable: 'Nie udało się wyświetlić tej karty.',
    replyKeepsSubject: '{name} zachowuje w odpowiedzi temat rozmowy.',
    bodyShortened: 'Tu skrócono. Otwórz w Gmailu, aby zobaczyć resztę.',
    writeAccessNeeded:
      '{name} ma ustawienie Odczyt, więc Poneva nie może wysłać tej wiadomości. Otwórz Połączenia, ustaw {name} na Odczyt i zapis, a potem wróć i wybierz Wyślij.',
    openConnections: 'Otwórz Połączenia',
    writeAccessAsk:
      '{name} ma ustawienie Odczyt i zmienić to może tylko właściciel lub administrator. Poproś ich o ustawienie {name} na Odczyt i zapis, a potem wybierz Wyślij.',
  },
  pt: {
    emailCard: 'Email',
    threadCard: 'Conversa de email',
    draftCard: 'Rascunho de email',
    from: 'De',
    to: 'Para',
    cc: 'Cc',
    bcc: 'Bcc',
    subject: 'Assunto',
    noSubject: '(sem assunto)',
    message: 'Mensagem',
    attachments: 'Anexos',
    showMore: 'Mostrar mais',
    showLess: 'Mostrar menos',
    messagesCount: 'one:mensagem|other:mensagens',
    earlierOmitted: 'one:mensagem anterior não mostrada|other:mensagens anteriores não mostradas',
    showCcBcc: 'Cc e Bcc',
    addRecipient: 'Adicionar um endereço',
    invalidAddress: 'Verifique o endereço {address}.',
    removeRecipient: 'Remover {address}',
    send: 'Enviar',
    sending: 'A enviar…',
    sent: 'Enviado',
    discard: 'Não enviar',
    discarded: 'Não enviado',
    failed: 'Não foi possível enviar este email.',
    unknown:
      'A Poneva não conseguiu confirmar se este email foi enviado. Verifique os Enviados em {name} antes de tentar novamente.',
    expired: 'Este rascunho expirou. Peça um novo à Poneva.',
    loading: 'A carregar…',
    unavailable: 'Não foi possível mostrar este cartão.',
    replyKeepsSubject: 'O {name} mantém o assunto da conversa numa resposta.',
    bodyShortened: 'Abreviado aqui. Abra-o no Gmail para ver o resto.',
    writeAccessNeeded:
      '{name} está definido como Leitura, por isso a Poneva não pode enviar este email. Abra Ligações, defina {name} como Leitura e escrita e depois volte e escolha Enviar.',
    openConnections: 'Abrir Ligações',
    writeAccessAsk:
      '{name} está definido como Leitura e só o proprietário ou um administrador o pode alterar. Peça-lhes que definam {name} como Leitura e escrita e depois escolha Enviar.',
  },
  ro: {
    emailCard: 'E-mail',
    threadCard: 'Conversație prin e-mail',
    draftCard: 'Ciornă de e-mail',
    from: 'De la',
    to: 'Către',
    cc: 'Cc',
    bcc: 'Bcc',
    subject: 'Subiect',
    noSubject: '(fără subiect)',
    message: 'Mesaj',
    attachments: 'Atașamente',
    showMore: 'Afișează mai mult',
    showLess: 'Afișează mai puțin',
    messagesCount: 'one:mesaj|few:mesaje|other:de mesaje',
    earlierOmitted:
      'one:mesaj anterior neafișat|few:mesaje anterioare neafișate|other:de mesaje anterioare neafișate',
    showCcBcc: 'Cc și Bcc',
    addRecipient: 'Adaugă o adresă',
    invalidAddress: 'Verifică adresa {address}.',
    removeRecipient: 'Elimină {address}',
    send: 'Trimite',
    sending: 'Se trimite…',
    sent: 'Trimis',
    discard: 'Nu trimite',
    discarded: 'Netrimis',
    failed: 'Acest e-mail nu a putut fi trimis.',
    unknown:
      'Poneva nu a putut confirma dacă acest e-mail a fost trimis. Verifică Trimise în {name} înainte să încerci din nou.',
    expired: 'Această ciornă a expirat. Cere-i Ponevei una nouă.',
    loading: 'Se încarcă…',
    unavailable: 'Acest card nu a putut fi afișat.',
    replyKeepsSubject: '{name} păstrează subiectul conversației într-un răspuns.',
    bodyShortened: 'Scurtat aici. Deschide-l în Gmail pentru rest.',
    writeAccessNeeded:
      '{name} este setat pe Citire, așa că Poneva nu poate trimite acest e-mail. Deschide Conexiuni, setează {name} pe Citire și scriere, apoi revino și alege Trimite.',
    openConnections: 'Deschide Conexiuni',
    writeAccessAsk:
      '{name} este setat pe Citire și doar proprietarul sau un administrator poate schimba asta. Roagă-i să seteze {name} pe Citire și scriere, apoi alege Trimite.',
  },
  sk: {
    emailCard: 'E-mail',
    threadCard: 'E-mailová konverzácia',
    draftCard: 'Koncept e-mailu',
    from: 'Od',
    to: 'Komu',
    cc: 'Kópia',
    bcc: 'Skrytá kópia',
    subject: 'Predmet',
    noSubject: '(bez predmetu)',
    message: 'Správa',
    attachments: 'Prílohy',
    showMore: 'Zobraziť viac',
    showLess: 'Zobraziť menej',
    messagesCount: 'one:správa|few:správy|many:správy|other:správ',
    earlierOmitted:
      'one:skoršia správa nie je zobrazená|few:skoršie správy nie sú zobrazené|many:skoršie správy nie sú zobrazené|other:skorších správ nie je zobrazených',
    showCcBcc: 'Kópia a skrytá kópia',
    addRecipient: 'Pridať adresu',
    invalidAddress: 'Skontrolujte adresu {address}.',
    removeRecipient: 'Odstrániť {address}',
    send: 'Odoslať',
    sending: 'Odosiela sa…',
    sent: 'Odoslané',
    discard: 'Neodosielať',
    discarded: 'Neodoslané',
    failed: 'Tento e-mail sa nepodarilo odoslať.',
    unknown:
      'Poneva nemohla overiť, či bol tento e-mail odoslaný. Pred ďalším pokusom skontrolujte Odoslané v {name}.',
    expired: 'Platnosť tohto konceptu vypršala. Požiadajte Ponevu o nový.',
    loading: 'Načítava sa…',
    unavailable: 'Túto kartu sa nepodarilo zobraziť.',
    replyKeepsSubject: '{name} v odpovedi ponechá predmet konverzácie.',
    bodyShortened: 'Tu je skrátené. Zvyšok otvorte v Gmaile.',
    writeAccessNeeded:
      '{name} je nastavený na Čítanie, takže Poneva tento e-mail nemôže odoslať. Otvorte Pripojenia, nastavte {name} na Čítanie a zápis, potom sa vráťte a zvoľte Odoslať.',
    openConnections: 'Otvoriť Pripojenia',
    writeAccessAsk:
      '{name} je nastavený na Čítanie a zmeniť to môže len jeho vlastník alebo správca. Požiadajte ich, aby nastavili {name} na Čítanie a zápis, a potom zvoľte Odoslať.',
  },
  sl: {
    emailCard: 'E-pošta',
    threadCard: 'Pogovor po e-pošti',
    draftCard: 'Osnutek e-pošte',
    from: 'Od',
    to: 'Za',
    cc: 'Kp',
    bcc: 'Skp',
    subject: 'Zadeva',
    noSubject: '(brez zadeve)',
    message: 'Sporočilo',
    attachments: 'Priloge',
    showMore: 'Pokaži več',
    showLess: 'Pokaži manj',
    messagesCount: 'one:sporočilo|two:sporočili|few:sporočila|other:sporočil',
    earlierOmitted:
      'one:prejšnje sporočilo ni prikazano|two:prejšnji sporočili nista prikazani|few:prejšnja sporočila niso prikazana|other:prejšnjih sporočil ni prikazanih',
    showCcBcc: 'Kp in Skp',
    addRecipient: 'Dodaj naslov',
    invalidAddress: 'Preverite naslov {address}.',
    removeRecipient: 'Odstrani {address}',
    send: 'Pošlji',
    sending: 'Pošiljanje…',
    sent: 'Poslano',
    discard: 'Ne pošlji',
    discarded: 'Ni poslano',
    failed: 'Tega e-poštnega sporočila ni bilo mogoče poslati.',
    unknown:
      'Poneva ni mogla potrditi, ali je bilo to sporočilo poslano. Preden poskusite znova, preverite Poslano v {name}.',
    expired: 'Ta osnutek je potekel. Prosite Poneva za novega.',
    loading: 'Nalaganje…',
    unavailable: 'Te kartice ni bilo mogoče prikazati.',
    replyKeepsSubject: '{name} v odgovoru ohrani zadevo pogovora.',
    bodyShortened: 'Tu je skrajšano. Preostanek odprite v Gmailu.',
    writeAccessNeeded:
      '{name} je nastavljen na Branje, zato Poneva tega e-poštnega sporočila ne more poslati. Odprite Povezave, nastavite {name} na Branje in pisanje, nato se vrnite in izberite Pošlji.',
    openConnections: 'Odpri Povezave',
    writeAccessAsk:
      '{name} je nastavljen na Branje in to lahko spremeni le lastnik ali skrbnik. Prosite jih, naj {name} nastavijo na Branje in pisanje, nato izberite Pošlji.',
  },
  es: {
    emailCard: 'Correo',
    threadCard: 'Conversación de correo',
    draftCard: 'Borrador de correo',
    from: 'De',
    to: 'Para',
    cc: 'Cc',
    bcc: 'Cco',
    subject: 'Asunto',
    noSubject: '(sin asunto)',
    message: 'Mensaje',
    attachments: 'Adjuntos',
    showMore: 'Mostrar más',
    showLess: 'Mostrar menos',
    messagesCount: 'one:mensaje|other:mensajes',
    earlierOmitted: 'one:mensaje anterior no mostrado|other:mensajes anteriores no mostrados',
    showCcBcc: 'Cc y Cco',
    addRecipient: 'Añadir una dirección',
    invalidAddress: 'Revisa la dirección {address}.',
    removeRecipient: 'Quitar {address}',
    send: 'Enviar',
    sending: 'Enviando…',
    sent: 'Enviado',
    discard: 'No enviar',
    discarded: 'No enviado',
    failed: 'No se pudo enviar este correo.',
    unknown:
      'Poneva no pudo confirmar si este correo se envió. Revisa Enviados en {name} antes de volver a intentarlo.',
    expired: 'Este borrador ha caducado. Pide uno nuevo a Poneva.',
    loading: 'Cargando…',
    unavailable: 'No se pudo mostrar esta tarjeta.',
    replyKeepsSubject: '{name} mantiene el asunto de la conversación en una respuesta.',
    bodyShortened: 'Acortado aquí. Ábrelo en Gmail para ver el resto.',
    writeAccessNeeded:
      '{name} está en Lectura, así que Poneva no puede enviar este correo. Abre Conexiones, cambia {name} a Lectura y escritura, y luego vuelve y elige Enviar.',
    openConnections: 'Abrir Conexiones',
    writeAccessAsk:
      '{name} está en Lectura y solo su propietario o un administrador puede cambiarlo. Pídeles que cambien {name} a Lectura y escritura y luego elige Enviar.',
  },
  sv: {
    emailCard: 'E-post',
    threadCard: 'E-postkonversation',
    draftCard: 'E-postutkast',
    from: 'Från',
    to: 'Till',
    cc: 'Kopia',
    bcc: 'Dold kopia',
    subject: 'Ämne',
    noSubject: '(inget ämne)',
    message: 'Meddelande',
    attachments: 'Bilagor',
    showMore: 'Visa mer',
    showLess: 'Visa mindre',
    messagesCount: 'one:meddelande|other:meddelanden',
    earlierOmitted: 'one:tidigare meddelande visas inte|other:tidigare meddelanden visas inte',
    showCcBcc: 'Kopia och dold kopia',
    addRecipient: 'Lägg till en adress',
    invalidAddress: 'Kontrollera adressen {address}.',
    removeRecipient: 'Ta bort {address}',
    send: 'Skicka',
    sending: 'Skickar…',
    sent: 'Skickat',
    discard: 'Skicka inte',
    discarded: 'Inte skickat',
    failed: 'Mejlet kunde inte skickas.',
    unknown:
      'Poneva kunde inte bekräfta om mejlet skickades. Kontrollera Skickat i {name} innan du försöker igen.',
    expired: 'Utkastet har gått ut. Be Poneva om ett nytt.',
    loading: 'Laddar…',
    unavailable: 'Kortet kunde inte visas.',
    replyKeepsSubject: '{name} behåller konversationens ämne i ett svar.',
    bodyShortened: 'Förkortat här. Öppna det i Gmail för resten.',
    writeAccessNeeded:
      '{name} är inställd på Läsning, så Poneva kan inte skicka det här mejlet. Öppna Anslutningar, ställ in {name} på Läsning och skrivning, kom sedan tillbaka och välj Skicka.',
    openConnections: 'Öppna Anslutningar',
    writeAccessAsk:
      '{name} är inställd på Läsning, och bara ägaren eller en administratör kan ändra det. Be dem ställa in {name} på Läsning och skrivning och välj sedan Skicka.',
  },
};
export default copy;
